// apps/web/src/components/analytics/AnalyticsAboutDrawer.tsx
//
// KAN-236 — operator-facing "About this data" surface for the Analytics page.
//
// Static, code-grounded copy explaining what each tab does, the time windows
// in play, confidence/coverage semantics, source-tier definitions, and the
// known data limitations a CISO-facing operator should understand before
// acting on these charts. Adds NO new API fields.
//
// Backed by the shared shadcn/ui Sheet primitive (same convention as
// dashboard/HelpDrawer.tsx). Self-contained: owns its own open state and
// trigger button so the AnalyticsPageShell only has to render <… /> in the
// header.
//
// Every factual claim below is grounded in source — see the inline // src:
// comments. Do not add claims that cannot be verified from the codebase.

import { Link } from 'react-router-dom'
import { Info } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'

// Confidence ladder labels — EXACT strings from
// components/analytics/ConfidenceLadder.tsx LABEL map (established →
// unverified). Descriptions paraphrase the canonical ontology in
// packages/shared/src/ontology/dimensions/story-confidence.ts.
const CONFIDENCE_BANDS: Array<{ label: string; meaning: string }> = [
  {
    label: 'Established',
    meaning:
      'Confirmed by at least one authoritative source, or 3+ independent research-tier orgs. Highest-trust story-level signal.',
  },
  {
    label: 'Corroborated',
    meaning:
      'Supported by 2+ research-tier orgs, or 1 research + 2+ specialist orgs. Multi-org independent confirmation.',
  },
  {
    label: 'Reported',
    meaning:
      'A single research-tier org, or 2+ specialist-tier orgs. One credible source without independent corroboration.',
  },
  {
    label: 'Single-source',
    meaning:
      'One specialist source, or 2+ general sources. The claim exists but is not yet corroborated by credible outlets.',
  },
  {
    label: 'Unverified',
    meaning:
      'A single general-tier source only. Weakest-trust signal — treat as provisional pending corroboration.',
  },
]

// Source-tier labels — EXACT strings from
// components/analytics/SourceTierMix.tsx TIER_LABEL map. Descriptions
// paraphrase packages/shared/src/ontology/dimensions/source-tiers.ts.
const SOURCE_TIERS: Array<{ label: string; meaning: string }> = [
  {
    label: 'Authoritative',
    meaning:
      'Primary, official sources — government CERTs, vendor PSIRTs, standards bodies (e.g. CISA, MSRC, MITRE, NVD).',
  },
  {
    label: 'Research',
    meaning:
      'Vendor threat-intelligence research arms and reputable security research groups (e.g. Mandiant, Google TAG, CrowdStrike Intelligence).',
  },
  {
    label: 'Specialist',
    meaning:
      'Independent cybersecurity trade press with editorial standards (e.g. BleepingComputer, Dark Reading, SecurityWeek, KrebsOnSecurity).',
  },
  {
    label: 'General',
    meaning:
      'General-interest tech/news sources that cover cybersecurity. The default for unclassified feeds.',
  },
]

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-sm font-semibold text-muted-foreground">{children}</h3>
  )
}

export function AnalyticsAboutDrawer() {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="About this Analytics data"
        >
          <Info className="h-4 w-4" aria-hidden />
        </Button>
      </SheetTrigger>

      <SheetContent
        side="right"
        className="flex w-[420px] flex-col gap-0 sm:w-[480px]"
      >
        <SheetHeader>
          <SheetTitle>About this data</SheetTitle>
          <SheetDescription>
            How to read the Analytics tabs and what their numbers do — and do
            not — represent.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-8 overflow-y-auto pr-2 text-sm">
          {/* Section 1 — tabs + time windows */}
          <section className="space-y-3">
            <SectionHeading>What these tabs do &amp; time windows</SectionHeading>
            {/* src: AnalyticsPageShell.tsx — three tabs (decide/trends/compare),
                Decide is the default. */}
            <dl className="space-y-2">
              <div>
                <dt className="font-medium text-foreground">Decide</dt>
                {/* src: DecideTab panels — PatchThisWeekPanel /
                    TopVictimCountriesPanel / PeerBenchmarkPanel all read
                    "last 7 days". AnalyticsPageShell §38 note: Decide is
                    range-agnostic, panels use spec-locked 7d windows. */}
                <dd className="text-muted-foreground">
                  Decision support — what to patch, who is being hit, and how
                  your sector compares. Every Decide panel is locked to a
                  fixed <strong>7d</strong> window; the range selector does not
                  apply here.
                </dd>
              </div>
              <div>
                <dt className="font-medium text-foreground">Trends</dt>
                {/* src: StoriesAnalyticsPage.tsx RANGES = ['7d','30d','90d'];
                    range selector at sp4-range-selector. */}
                <dd className="text-muted-foreground">
                  Story Coverage — how broad and corroborated the IntelHub feed
                  is, and how the landscape is moving. Use the range selector to
                  switch between <strong>7d</strong>, <strong>30d</strong> and{' '}
                  <strong>90d</strong> windows.
                </dd>
              </div>
              <div>
                <dt className="font-medium text-foreground">Compare</dt>
                {/* src: AnalyticsPageShell.tsx — Compare receives the shared
                    `range`; CompareTab.tsx uses useCompareAnalytics(range, …). */}
                <dd className="text-muted-foreground">
                  Cross-sector comparison — incident, confidence and regulatory
                  views by sector. Shares the same <strong>7d</strong>/
                  <strong>30d</strong>/<strong>90d</strong> range as Trends.
                </dd>
              </div>
            </dl>
          </section>

          {/* Section 2 — confidence & coverage semantics */}
          <section className="space-y-3">
            <SectionHeading>Confidence &amp; coverage semantics</SectionHeading>
            <p className="text-muted-foreground">
              Story confidence is the structural corroboration axis — it is
              computed from the mix of source tiers behind a story cluster, not
              from any single article&rsquo;s wording. Confidence labels are not paid
              placement or vendor popularity scores. See{' '}
              <Link
                to="/trust"
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Who pays CyberFind?
              </Link>{' '}
              for the trust and independence policy.
            </p>
            <dl className="space-y-2">
              {CONFIDENCE_BANDS.map((band) => (
                <div key={band.label}>
                  <dt className="font-medium text-foreground">{band.label}</dt>
                  <dd className="text-muted-foreground">{band.meaning}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/* Section 3 — data source notes (tiers) */}
          <section className="space-y-3">
            <SectionHeading>Data source notes</SectionHeading>
            <p className="text-muted-foreground">
              Sources are classified into four tiers, most authoritative first.
              The Source tier mix and the confidence ladder above both derive
              from these tiers:
            </p>
            <dl className="space-y-2">
              {SOURCE_TIERS.map((tier) => (
                <div key={tier.label}>
                  <dt className="font-medium text-foreground">{tier.label}</dt>
                  <dd className="text-muted-foreground">{tier.meaning}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/* Section 4 — known limitations (only code-verifiable claims) */}
          <section className="space-y-3">
            <SectionHeading>Known limitations</SectionHeading>
            <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
              {/* src: SectorIncidentHeatmap.tsx — caption: "Grouped by article
                  industry tags, not victim-sector incident claims." */}
              <li>
                The Compare &rarr; Sector incident heatmap counts stories by{' '}
                <strong>article industry tags, not victim-sector</strong>{' '}
                incident claims. Tag-based sector counts can differ from who was
                actually named as a victim.
              </li>
              {/* src: RegulatoryExposureBySector.tsx — caption: sector picker
                  does not filter the regulatory chart. */}
              <li>
                Compare&rsquo;s regulatory-exposure chart counts articles
                carrying a jurisdiction tag across all IntelHub stories — the
                sector picker does not filter it.
              </li>
              {/* src: TopVictimCountriesPanel.tsx — gated on geoSufficiency;
                  renders TopVictimSectorsPanel fallback when insufficient. */}
              <li>
                Decide&rsquo;s Top victim countries panel falls back to a
                victim-<em>sectors</em> view when there is not enough geographic
                signal in the window.
              </li>
              {/* src: ConfidenceLadder.tsx / SourceTierMix.tsx — both are scoped
                  to "the selected range"; empty-state copy references it. */}
              <li>
                All Trends figures are scoped to the selected range. A short
                window with few stories will read as low confidence simply
                because there is less to corroborate.
              </li>
            </ul>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  )
}
